/**
 * BrandingModal
 *
 * Full-featured branding configuration hub. Users set their agency identity,
 * logo, brand colours, font, and watermark preferences here. Settings are
 * persisted via the parent (App.tsx → localStorage) and applied to every
 * floor-plan export.
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Palette,
  Type,
  Image as ImageIcon,
  Building2,
  Stamp,
  Check,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  ChevronDown,
  Sparkles,
  LayoutTemplate,
} from 'lucide-react';
import {
  BrandingProfile,
  DEFAULT_BRANDING_PROFILE,
  WATERMARK_POSITIONS,
  BRAND_FONTS,
  BRAND_COLOR_PRESETS,
  WatermarkPosition,
} from '../types';

// ============================================================================
// PROPS
// ============================================================================

interface BrandingModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: BrandingProfile;
  onChange: (profile: BrandingProfile) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

type Tab = 'identity' | 'colours' | 'fonts' | 'watermark';

const TAB_CONFIG: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'identity',  label: 'Identity',  icon: Building2 },
  { id: 'colours',   label: 'Colours',   icon: Palette },
  { id: 'fonts',     label: 'Fonts',     icon: Type },
  { id: 'watermark', label: 'Watermark', icon: Stamp },
];

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">{children}</p>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-teal-600' : 'bg-slate-600'}`}
  >
    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'left-6' : 'left-1'}`} />
  </button>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BrandingModal: React.FC<BrandingModalProps> = ({ isOpen, onClose, profile, onChange }) => {
  const [activeTab, setActiveTab] = useState<Tab>('identity');
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const styleRefInputRef = useRef<HTMLInputElement>(null);

  const set = useCallback(<K extends keyof BrandingProfile>(key: K, value: BrandingProfile[K]) => {
    onChange({ ...profile, [key]: value });
  }, [profile, onChange]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      set('logoDataUrl', ev.target?.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleStyleRefUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      set('styleReferenceDataUrl', ev.target?.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const applyPreset = (preset: typeof BRAND_COLOR_PRESETS[number]) => {
    onChange({
      ...profile,
      primaryColor: preset.primary,
      accentColor: preset.accent,
      footerBgColor: preset.footerBg,
      footerTextColor: preset.footerText,
    });
  };

  if (!isOpen) return null;

  // ── watermark position normalised coords
  const positionCoords: Record<WatermarkPosition, { x: number; y: number }> = {
    'top-left':     { x: 0.02, y: 0.02 },
    'top-right':    { x: 1 - profile.watermarkScale - 0.02, y: 0.02 },
    'bottom-left':  { x: 0.02, y: 1 - profile.watermarkScale * 0.5 - 0.02 },
    'bottom-right': { x: 1 - profile.watermarkScale - 0.02, y: 1 - profile.watermarkScale * 0.5 - 0.02 },
    'center':       { x: 0.5 - profile.watermarkScale / 2, y: 0.5 - profile.watermarkScale * 0.25 },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-900/50 rounded-lg">
              <Sparkles size={20} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Branding</h3>
              <p className="text-xs text-slate-400">Personalise your floor plan exports</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-slate-700 shrink-0 overflow-x-auto">
          {TAB_CONFIG.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 flex-1 min-w-[80px] px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap
                ${activeTab === id
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-slate-800/30'
                  : 'text-slate-400 hover:text-white'
                }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* ═══════════════════ IDENTITY ═══════════════════ */}
          {activeTab === 'identity' && (
            <>
              {/* Logo upload */}
              <div>
                <SectionLabel>Agency Logo</SectionLabel>
                <div className="flex items-start gap-4">
                  <div
                    className={`w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center shrink-0 overflow-hidden cursor-pointer transition-colors
                      ${profile.logoDataUrl ? 'border-purple-500/50 bg-slate-800' : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'}`}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {profile.logoDataUrl
                      ? <img src={profile.logoDataUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                      : <div className="flex flex-col items-center gap-1 text-slate-500">
                          <Upload size={20} />
                          <span className="text-[10px]">Upload</span>
                        </div>
                    }
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
                    >
                      <Upload size={14} />
                      {profile.logoDataUrl ? 'Replace Logo' : 'Upload Logo'}
                    </button>
                    {profile.logoDataUrl && (
                      <button
                        onClick={() => set('logoDataUrl', undefined)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-slate-500">PNG, SVG or JPEG recommended.<br />Transparent PNG works best.</p>
                  </div>
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </div>

              {/* Floor plan style reference */}
              <div>
                <SectionLabel>Floor Plan Style to Copy <span className="normal-case tracking-normal text-slate-600">(optional)</span></SectionLabel>
                <div className="flex items-start gap-4">
                  <div
                    className={`w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center shrink-0 overflow-hidden cursor-pointer transition-colors
                      ${profile.styleReferenceDataUrl ? 'border-purple-500/50 bg-slate-800' : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'}`}
                    onClick={() => styleRefInputRef.current?.click()}
                  >
                    {profile.styleReferenceDataUrl
                      ? <img src={profile.styleReferenceDataUrl} alt="Style reference" className="w-full h-full object-contain p-1" />
                      : <div className="flex flex-col items-center gap-1 text-slate-500">
                          <LayoutTemplate size={20} />
                          <span className="text-[10px]">Upload</span>
                        </div>
                    }
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    <button
                      onClick={() => styleRefInputRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
                    >
                      <Upload size={14} />
                      {profile.styleReferenceDataUrl ? 'Replace Plan' : 'Upload Plan'}
                    </button>
                    {profile.styleReferenceDataUrl && (
                      <button
                        onClick={() => set('styleReferenceDataUrl', undefined)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-slate-500">Upload an example floor plan and<br />the AI will match its style.</p>
                  </div>
                </div>
                <input ref={styleRefInputRef} type="file" accept="image/*" className="hidden" onChange={handleStyleRefUpload} />
              </div>

              {/* Agency name */}
              <div>
                <SectionLabel>Agency Name</SectionLabel>
                <input
                  type="text"
                  value={profile.agencyName}
                  onChange={(e) => set('agencyName', e.target.value)}
                  placeholder="e.g., Smith & Partners"
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Tagline */}
              <div>
                <SectionLabel>Tagline <span className="normal-case tracking-normal text-slate-600">(optional)</span></SectionLabel>
                <input
                  type="text"
                  value={profile.tagline ?? ''}
                  onChange={(e) => set('tagline', e.target.value || undefined)}
                  placeholder="e.g., Premium Property Specialists"
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Footer options */}
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 space-y-3">
                <SectionLabel>Footer options</SectionLabel>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Show logo in export footer</p>
                    <p className="text-xs text-slate-400">Prints your logo alongside the disclaimer</p>
                  </div>
                  <Toggle checked={profile.showLogoInFooter} onChange={(v) => set('showLogoInFooter', v)} />
                </div>
              </div>

              {/* Live preview */}
              {(profile.agencyName || profile.logoDataUrl) && (
                <div>
                  <SectionLabel>Footer Preview</SectionLabel>
                  <div
                    className="rounded-lg p-4 text-sm"
                    style={{ backgroundColor: profile.footerBgColor, color: profile.footerTextColor, fontFamily: profile.fontFamily }}
                  >
                    <div className="flex items-center gap-3 border-b pb-3 mb-2" style={{ borderColor: profile.primaryColor + '55' }}>
                      {profile.showLogoInFooter && profile.logoDataUrl && (
                        <img src={profile.logoDataUrl} alt="logo" className="h-8 w-auto object-contain" />
                      )}
                      <div>
                        {profile.agencyName && <p className="font-bold text-sm" style={{ color: profile.footerTextColor }}>{profile.agencyName}</p>}
                        {profile.tagline && <p className="text-xs opacity-70">{profile.tagline}</p>}
                      </div>
                    </div>
                    <p className="text-[10px] opacity-60 leading-relaxed">
                      APPROX. GROSS INTERNAL FLOOR AREA — sq ft / — sqm{'\n'}This floor plan is for illustrative purposes only.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ═══════════════════ COLOURS ═══════════════════ */}
          {activeTab === 'colours' && (
            <>
              {/* Presets */}
              <div>
                <SectionLabel>Colour Presets</SectionLabel>
                <div className="grid grid-cols-4 gap-2">
                  {BRAND_COLOR_PRESETS.map((preset) => {
                    const isActive =
                      profile.primaryColor === preset.primary &&
                      profile.accentColor === preset.accent;
                    return (
                      <button
                        key={preset.name}
                        onClick={() => applyPreset(preset)}
                        className={`relative p-2 rounded-xl border-2 transition-all text-center
                          ${isActive ? 'border-purple-500' : 'border-slate-700 hover:border-slate-500'}`}
                      >
                        <div className="flex gap-1 mb-1.5 justify-center">
                          <div className="w-5 h-5 rounded-full" style={{ background: preset.primary }} />
                          <div className="w-5 h-5 rounded-full" style={{ background: preset.accent }} />
                        </div>
                        <span className="text-[10px] text-slate-400">{preset.name}</span>
                        {isActive && (
                          <div className="absolute top-1 right-1">
                            <Check size={10} className="text-purple-400" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom colours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <SectionLabel>Primary Colour</SectionLabel>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={profile.primaryColor}
                      onChange={(e) => set('primaryColor', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-slate-600 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={profile.primaryColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) set('primaryColor', e.target.value);
                      }}
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <SectionLabel>Accent Colour</SectionLabel>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={profile.accentColor}
                      onChange={(e) => set('accentColor', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-slate-600 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={profile.accentColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) set('accentColor', e.target.value);
                      }}
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <SectionLabel>Footer Background</SectionLabel>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={profile.footerBgColor}
                      onChange={(e) => set('footerBgColor', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-slate-600 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={profile.footerBgColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) set('footerBgColor', e.target.value);
                      }}
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <SectionLabel>Footer Text</SectionLabel>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={profile.footerTextColor}
                      onChange={(e) => set('footerTextColor', e.target.value)}
                      className="w-10 h-10 rounded-lg border border-slate-600 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={profile.footerTextColor}
                      onChange={(e) => {
                        if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) set('footerTextColor', e.target.value);
                      }}
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Canvas colour options */}
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 space-y-3">
                <SectionLabel>Apply to floor plan canvas</SectionLabel>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Colour wall lines</p>
                    <p className="text-xs text-slate-400">Use primary colour for wall strokes</p>
                  </div>
                  <Toggle checked={profile.useColoredWalls} onChange={(v) => set('useColoredWalls', v)} />
                </div>
              </div>

              {/* Colour preview strip */}
              <div>
                <SectionLabel>Colour Preview</SectionLabel>
                <div className="rounded-xl overflow-hidden border border-slate-700">
                  <div className="h-10" style={{ background: profile.primaryColor }} />
                  <div className="h-6" style={{ background: profile.accentColor }} />
                  <div
                    className="p-3 flex items-center justify-between"
                    style={{ background: profile.footerBgColor, color: profile.footerTextColor }}
                  >
                    <span className="text-xs font-medium">{profile.agencyName || 'Agency Name'}</span>
                    <span className="text-[10px] opacity-60">footer</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════ FONTS ═══════════════════ */}
          {activeTab === 'fonts' && (
            <>
              <div>
                <SectionLabel>Font Family</SectionLabel>
                <div className="relative">
                  <button
                    onClick={() => setFontDropdownOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white hover:border-slate-500 transition-colors"
                    style={{ fontFamily: profile.fontFamily }}
                  >
                    <span>{BRAND_FONTS.find(f => f.value === profile.fontFamily)?.label ?? profile.fontFamily}</span>
                    <ChevronDown size={16} className={`transition-transform ${fontDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {fontDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-xl overflow-hidden">
                      {['System', 'Google'].map((category) => (
                        <div key={category}>
                          <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600 bg-slate-800/50">
                            {category} Fonts
                          </div>
                          {BRAND_FONTS.filter(f => f.category === category).map((font) => (
                            <button
                              key={font.value}
                              onClick={() => { set('fontFamily', font.value); setFontDropdownOpen(false); }}
                              className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-800 transition-colors
                                ${profile.fontFamily === font.value ? 'bg-purple-900/30 text-purple-300' : 'text-slate-200'}`}
                              style={{ fontFamily: font.value }}
                            >
                              <span>{font.label}</span>
                              {profile.fontFamily === font.value && <Check size={14} className="text-purple-400" />}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Canvas font option */}
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 space-y-3">
                <SectionLabel>Apply to floor plan canvas</SectionLabel>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Use brand font for labels</p>
                    <p className="text-xs text-slate-400">Apply to room names & measurements</p>
                  </div>
                  <Toggle checked={profile.useBrandFont} onChange={(v) => set('useBrandFont', v)} />
                </div>
              </div>

              {/* Font preview */}
              <div>
                <SectionLabel>Font Preview</SectionLabel>
                <div
                  className="p-5 rounded-xl bg-slate-800 border border-slate-700 space-y-1"
                  style={{ fontFamily: profile.fontFamily }}
                >
                  <p className="text-2xl font-bold text-white">{profile.agencyName || 'Agency Name'}</p>
                  {profile.tagline && <p className="text-sm text-slate-400">{profile.tagline}</p>}
                  <p className="text-xs text-slate-500 mt-2">
                    Living Room · Kitchen · Master Bedroom · Bathroom
                  </p>
                  <p className="text-[10px] text-slate-600 mt-1">
                    5.2m × 4.1m · 985 sq ft · ≈ 91 sqm
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════ WATERMARK ═══════════════════ */}
          {activeTab === 'watermark' && (
            <>
              {/* Enable toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Enable Watermark</h4>
                  <p className="text-xs text-slate-400 mt-0.5">Overlay your logo on image exports</p>
                </div>
                <Toggle checked={profile.watermarkEnabled} onChange={(v) => set('watermarkEnabled', v)} />
              </div>

              {profile.watermarkEnabled && (
                <>
                  {/* Logo check */}
                  {!profile.logoDataUrl && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-900/20 border border-amber-700/40 text-amber-300 text-sm">
                      <ImageIcon size={16} className="shrink-0" />
                      Upload a logo on the Identity tab to enable watermarking.
                    </div>
                  )}

                  {/* Position */}
                  <div>
                    <SectionLabel>Position</SectionLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {WATERMARK_POSITIONS.map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => set('watermarkPosition', value)}
                          className={`py-2.5 px-3 rounded-xl border-2 text-sm transition-all text-center
                            ${profile.watermarkPosition === value
                              ? 'border-purple-500 bg-purple-900/30 text-purple-300'
                              : 'border-slate-700 bg-slate-800/50 hover:border-slate-500 text-slate-300'
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Opacity */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <SectionLabel>Opacity</SectionLabel>
                      <span className="text-sm text-slate-300">{Math.round(profile.watermarkOpacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={profile.watermarkOpacity}
                      onChange={(e) => set('watermarkOpacity', Number(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                        [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full"
                    />
                  </div>

                  {/* Scale */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <SectionLabel>Size</SectionLabel>
                      <span className="text-sm text-slate-300">{Math.round(profile.watermarkScale * 100)}% of image width</span>
                    </div>
                    <input
                      type="range"
                      min="0.05"
                      max="0.50"
                      step="0.01"
                      value={profile.watermarkScale}
                      onChange={(e) => set('watermarkScale', Number(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                        [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:rounded-full"
                    />
                  </div>

                  {/* Visual position preview */}
                  {profile.logoDataUrl && (
                    <div>
                      <SectionLabel>Position Preview</SectionLabel>
                      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-700 border border-slate-600">
                        {/* Fake floor-plan grid */}
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                          backgroundSize: '20px 20px'
                        }} />
                        <div className="absolute inset-4 border border-white/30 rounded" />
                        {/* Logo preview */}
                        <img
                          src={profile.logoDataUrl}
                          alt="Watermark preview"
                          style={{
                            position: 'absolute',
                            left: `${positionCoords[profile.watermarkPosition].x * 100}%`,
                            top: `${positionCoords[profile.watermarkPosition].y * 100}%`,
                            width: `${profile.watermarkScale * 100}%`,
                            opacity: profile.watermarkOpacity,
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ── Footer Actions ── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/30 shrink-0">
          <button
            onClick={() => onChange(DEFAULT_BRANDING_PROFILE)}
            className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            Reset to defaults
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
            >
              <Check size={16} />
              Save Branding
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingModal;
